import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Award, LogOut, FileText, CheckCircle, Clock, 
  ChevronDown, ChevronUp, Send
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SponsorDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedApp, setExpandedApp] = useState(null);
  const [endorseDialog, setEndorseDialog] = useState({ open: false, applicationId: null });
  const [endorsementText, setEndorsementText] = useState('');
  const [coChairName, setCoChairName] = useState('');

  useEffect(() => {
    fetchApplications();
  }, []);

  const fetchApplications = async () => {
    try {
      const response = await axios.get(`${API}/applications`);
      setApplications(response.data);
    } catch (error) {
      toast.error('Failed to load applications');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
  };

  const openEndorseDialog = (applicationId) => {
    setEndorseDialog({ open: true, applicationId });
    setEndorsementText('');
    setCoChairName(user?.first_name + ' ' + user?.last_name);
  };

  const submitEndorsement = async () => {
    if (!endorsementText.trim() || !coChairName.trim()) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      await axios.post(`${API}/applications/${endorseDialog.applicationId}/endorse`, {
        endorsement_text: endorsementText,
        co_chair_name: coChairName
      });
      toast.success('Application endorsed successfully!');
      setEndorseDialog({ open: false, applicationId: null });
      fetchApplications();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to endorse');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-12 h-12 border-4 border-greek-blue border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-greek-blue rounded-sm flex items-center justify-center">
              <Award className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-heading text-lg font-bold text-greek-blue">AHEPA Family</h1>
              <p className="text-xs text-slate-500">Sponsor Portal</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="font-semibold text-slate-900">{user?.first_name} {user?.last_name}</p>
              <p className="text-sm text-slate-500">Sponsor</p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleLogout}
              className="rounded-sm border-slate-300"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-8">
          <h2 className="font-heading text-3xl font-bold text-slate-900 mb-2">
            Applications to Review
          </h2>
          <p className="text-slate-600">
            Review and endorse scholarship applications assigned to you.
          </p>
        </div>

        {applications.length === 0 ? (
          <div className="bg-white rounded-sm border border-slate-200 p-12 text-center">
            <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="font-heading text-xl font-semibold text-slate-700 mb-2">No Applications</h3>
            <p className="text-slate-500">You have no applications to review at this time.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {applications.map((app) => (
              <div
                key={app.id}
                className="bg-white rounded-sm border border-slate-200 overflow-hidden"
                data-testid={`sponsor-app-card-${app.id}`}
              >
                <div
                  className="p-6 cursor-pointer hover:bg-slate-50 transition-colors"
                  onClick={() => setExpandedApp(expandedApp === app.id ? null : app.id)}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-heading text-lg font-semibold text-slate-900">
                        {app.applicant_info?.first_name} {app.applicant_info?.family_name}
                      </h3>
                      <p className="text-sm text-slate-500">{app.scholarship_name}</p>
                    </div>
                    <div className="flex items-center gap-4">
                      {app.sponsor_endorsement ? (
                        <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-sm text-sm font-medium bg-emerald-100 text-emerald-700">
                          <CheckCircle className="w-4 h-4" />
                          Endorsed
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-sm text-sm font-medium bg-amber-100 text-amber-700">
                          <Clock className="w-4 h-4" />
                          Pending Review
                        </span>
                      )}
                      {expandedApp === app.id ? (
                        <ChevronUp className="w-5 h-5 text-slate-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-slate-400" />
                      )}
                    </div>
                  </div>
                </div>

                {expandedApp === app.id && (
                  <div className="border-t border-slate-200 p-6 bg-slate-50">
                    <div className="grid md:grid-cols-2 gap-6">
                      {/* Personal Info */}
                      <div>
                        <h4 className="font-semibold text-slate-800 mb-3">Personal Information</h4>
                        <div className="space-y-2 text-sm">
                          <p><span className="text-slate-500">Name:</span> {app.applicant_info?.first_name} {app.applicant_info?.middle_name} {app.applicant_info?.family_name}</p>
                          <p><span className="text-slate-500">Email:</span> {app.applicant_info?.email}</p>
                          <p><span className="text-slate-500">Father:</span> {app.applicant_info?.father_name}</p>
                          <p><span className="text-slate-500">Mother:</span> {app.applicant_info?.mother_name}</p>
                        </div>
                      </div>

                      {/* Academic Info */}
                      <div>
                        <h4 className="font-semibold text-slate-800 mb-3">Academic Information</h4>
                        <div className="space-y-2 text-sm">
                          <p><span className="text-slate-500">Institution:</span> {app.academic_info?.current_institution}</p>
                          <p><span className="text-slate-500">Program:</span> {app.academic_info?.program}</p>
                          <p><span className="text-slate-500">Year:</span> {app.academic_info?.year_of_study}</p>
                        </div>
                      </div>

                      {/* Greek Heritage */}
                      <div className="md:col-span-2">
                        <h4 className="font-semibold text-slate-800 mb-3">Greek Heritage</h4>
                        <p className="text-sm text-slate-600 bg-white p-4 rounded-sm">
                          {app.applicant_info?.greek_heritage || 'Not provided'}
                        </p>
                      </div>

                      {/* Aspirations */}
                      <div className="md:col-span-2">
                        <h4 className="font-semibold text-slate-800 mb-3">Long Term Aspirations</h4>
                        <p className="text-sm text-slate-600 bg-white p-4 rounded-sm">
                          {app.applicant_info?.long_term_aspirations || 'Not provided'}
                        </p>
                      </div>
                    </div>

                    {!app.sponsor_endorsement && (
                      <div className="mt-6 pt-6 border-t border-slate-200">
                        <Button
                          onClick={() => openEndorseDialog(app.id)}
                          className="bg-greek-gold hover:bg-greek-gold-dark text-white rounded-sm"
                          data-testid={`endorse-btn-${app.id}`}
                        >
                          <Send className="w-4 h-4 mr-2" />
                          Endorse This Application
                        </Button>
                      </div>
                    )}

                    {app.sponsor_endorsement && (
                      <div className="mt-6 pt-6 border-t border-slate-200">
                        <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-sm">
                          <h4 className="font-semibold text-emerald-800 mb-2">Your Endorsement</h4>
                          <p className="text-sm text-slate-700 italic">"{app.sponsor_endorsement.text}"</p>
                          <p className="text-sm text-emerald-600 mt-2">
                            - {app.sponsor_endorsement.co_chair_name}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Endorse Dialog */}
      <Dialog open={endorseDialog.open} onOpenChange={(open) => setEndorseDialog({ ...endorseDialog, open })}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">Endorse Application</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Your Name (Co-Chair)</Label>
              <Input
                value={coChairName}
                onChange={(e) => setCoChairName(e.target.value)}
                className="rounded-sm"
                data-testid="input-cochair-name"
              />
            </div>
            <div className="space-y-2">
              <Label>Endorsement Statement</Label>
              <Textarea
                value={endorsementText}
                onChange={(e) => setEndorsementText(e.target.value)}
                placeholder="Write your endorsement for this applicant..."
                className="rounded-sm min-h-[150px]"
                data-testid="textarea-endorsement"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEndorseDialog({ open: false, applicationId: null })} className="rounded-sm">
              Cancel
            </Button>
            <Button onClick={submitEndorsement} className="bg-greek-gold hover:bg-greek-gold-dark text-white rounded-sm" data-testid="submit-endorsement-btn">
              Submit Endorsement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SponsorDashboard;
